import { StrictMode, useEffect } from 'react';
import { createRoot } from 'react-dom/client';
import { Security } from '@okta/okta-react';
import type { OktaAuth } from '@okta/okta-auth-js';
import { DeckApp } from '@/framework/DeckApp';
import { EditLayout } from '@/framework/EditLayout';
import { PresenterLayout } from '@/framework/PresenterLayout';
import { usePresenterSync } from '@/framework/usePresenterSync';
import { Home } from '@/Home';
import { decks, decksBySlug, isDeckPublic } from '@/registry';
import type { Deck } from '@/framework/types';
import { useLocation, navigate } from '@/router';
import { oktaAuth, oktaConfigError, isDevAuthBypass } from '@/auth/oktaConfig';
import { AuthSplash } from '@/auth/AuthSplash';
import { RequireAuth } from '@/auth/RequireAuth';
import { LoginCallbackRoute } from '@/auth/LoginCallbackRoute';
import '@/framework/styles/deck.css';
import '@/framework/styles/layouts.css';

/**
 * Audience window — fullscreens the deck and follows the presenter's slide via
 * BroadcastChannel (scoped to `deck.slug`). Reuses the standard slide chrome so
 * the audience sees the same section/slide-number readout as a live deck.
 */
function AudienceView({ deck }: { deck: Deck }) {
  const { idx } = usePresenterSync(deck.slug, 'audience', 0);
  const { slides } = deck;
  const total = slides.length;

  useEffect(() => {
    if (!document.fullscreenElement) {
      void document.documentElement.requestFullscreen().catch(() => {});
    }
  }, []);

  return (
    <main className="deck is-audience" id="deck" data-deck={deck.slug}>
      {slides.map((slide, i) => {
        const isActive = i === idx;
        return (
          <section
            key={slide.id}
            className={`slide sn-section-${slide.theme}${isActive ? ' is-active' : ''}`}
          >
            <header className="chrome">
              <div className="chrome-logo">
                <img src="/assets/logomark-blue.svg" alt="" aria-hidden="true" />
                <span>{slide.section}</span>
              </div>
              <div className="slide-num">
                {String(slide.id).padStart(2, '0')} <span className="of">/ {String(total).padStart(2, '0')}</span>
              </div>
            </header>
            {slide.render()}
          </section>
        );
      })}
    </main>
  );
}

function App() {
  const loc = useLocation();
  const path = loc.pathname;
  const slug = path.replace(/^\/+|\/+$/g, '');
  const params = new URLSearchParams(loc.search);
  const isPreview = params.has('preview');
  const isPresenter = params.has('presenter');
  const isAudience = params.has('audience');
  const isEdit = params.has('edit') && import.meta.env.DEV;
  const deck = slug ? decksBySlug.get(slug) : undefined;

  useEffect(() => {
    if (isPresenter && deck) document.title = `Presenter — ${deck.title}`;
    else if (isEdit && deck) document.title = `Editing ${deck.title} — StreamNative`;
    else document.title = deck ? `${deck.title} — StreamNative` : 'StreamNative Decks';
  }, [deck, isEdit, isPresenter]);

  if (path === '/login/callback') return <LoginCallbackRoute />;

  // Presenter mode — two-pane view with current + next slide and speaker notes.
  if (isPresenter && deck) {
    if (isDeckPublic(deck)) return <PresenterLayout deck={deck} />;
    return (
      <RequireAuth>
        <PresenterLayout deck={deck} />
      </RequireAuth>
    );
  }

  // Audience mode — synced to the presenter via BroadcastChannel, no HUD.
  if (isAudience && deck) {
    if (isDeckPublic(deck)) return <AudienceView deck={deck} />;
    return (
      <RequireAuth>
        <AudienceView deck={deck} />
      </RequireAuth>
    );
  }

  // Edit mode (dev-only) — auth-gated like the rest of the app.
  if (isEdit && deck) {
    return (
      <RequireAuth>
        <EditLayout deckSlug={slug} />
      </RequireAuth>
    );
  }

  if (deck && isDeckPublic(deck)) {
    return <DeckApp deck={deck} preview={isPreview} />;
  }

  return (
    <RequireAuth>
      {deck ? <DeckApp deck={deck} preview={isPreview} /> : <Home decks={decks} />}
    </RequireAuth>
  );
}

async function restoreOriginalUri(_oktaAuth: OktaAuth, originalUri?: string) {
  navigate(originalUri || '/', { replace: true });
}

const container = document.getElementById('root');
if (!container) throw new Error('Root container #root not found');

const root = createRoot(container);

if (isDevAuthBypass) {
  root.render(
    <StrictMode>
      <App />
    </StrictMode>,
  );
} else if (oktaConfigError || !oktaAuth) {
  root.render(
    <StrictMode>
      <AuthSplash
        tone="error"
        label="Okta is not configured"
        detail={oktaConfigError ?? 'oktaAuth instance is unavailable.'}
      />
    </StrictMode>,
  );
} else {
  root.render(
    <StrictMode>
      <Security oktaAuth={oktaAuth} restoreOriginalUri={restoreOriginalUri}>
        <App />
      </Security>
    </StrictMode>,
  );
}
